import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Card, CardActionArea, CardContent, LinearProgress, Stack, Typography } from '@mui/material';
import { useGetIdentity } from '@refinedev/core';
import type { AdminUnit, PublicBooking, PublicComplaint, PublicEmergencyAlert, PublicInvoice, PublicResident } from '@residenone/shared';
import { listAdminUnits } from '../lib/units-api';
import { listResidents } from '../lib/residents-api';
import { fetchAllInvoices, listOpsEmergency, unitBillingFrom } from '../lib/ops-api';
import { complaintsThisMonth, fetchRecentComplaints } from '../lib/complaints-summary';
import { listAdminBookings } from '../lib/facilities-api';
import { buildBillingInsights } from '../lib/billing-insights';
import { averageResolveHours } from '../lib/dashboard-insights';
import { activityFeed, collectionPace, groupActions } from '../lib/dashboard-health';
import { summarizePeople } from '../lib/people-summary';
import { HealthTiles, type HealthNumbers } from '../components/dashboard/HealthTiles';
import { GroupedActions } from '../components/dashboard/GroupedActions';
import { CollectionPaceCard } from '../components/dashboard/CollectionPaceCard';
import { ActivityFeed } from '../components/dashboard/ActivityFeed';
import { useT } from '../i18n';

interface Identity {
  name: string;
}

/** Each source on its own: null while loading or when this user can't read it. */
interface Sources {
  units: AdminUnit[] | null;
  invoices: PublicInvoice[] | null;
  residents: PublicResident[] | null;
  complaints: PublicComplaint[] | null;
  bookings: PublicBooking[] | null;
  emergencies: PublicEmergencyAlert[] | null;
}

const EMPTY: Sources = { units: null, invoices: null, residents: null, complaints: null, bookings: null, emergencies: null };

function greetingKey(): string {
  const h = new Date().getHours();
  if (h < 11) return 'dash.greetingMorning';
  if (h < 18) return 'dash.greetingAfternoon';
  return 'dash.greetingEvening';
}

export function DashboardPage() {
  const { data: identity } = useGetIdentity<Identity>();
  const t = useT();
  const [src, setSrc] = useState<Sources>(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const retry = useCallback(() => setReloadKey((k) => k + 1), []);
  const today = useMemo(() => new Date(), [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let active = true;
    setLoaded(false);
    // Each list is fetched once and shared by every card. Any of them can 403
    // for staff without that permission; that card then shows "—".
    void Promise.allSettled([
      listAdminUnits(),
      fetchAllInvoices(),
      listResidents(),
      fetchRecentComplaints(new Date()),
      listAdminBookings({ status: 'PENDING_APPROVAL', limit: 100 }).then((r) => r.items),
      listOpsEmergency(100),
    ]).then(([units, invoices, residents, complaints, bookings, emergencies]) => {
      if (!active) return;
      const ok = <T,>(r: PromiseSettledResult<T>) => (r.status === 'fulfilled' ? r.value : null);
      setSrc({
        units: ok(units),
        invoices: ok(invoices),
        residents: ok(residents),
        complaints: ok(complaints),
        bookings: ok(bookings),
        emergencies: ok(emergencies),
      });
      setLoaded(true);
    });
    return () => { active = false; };
  }, [reloadKey]);

  const billing = useMemo(
    () => (src.units && src.invoices ? buildBillingInsights(unitBillingFrom(src.units, src.invoices), today, 6) : null),
    [src.units, src.invoices, today],
  );
  const pace = useMemo(() => (src.invoices ? collectionPace(src.invoices, today) : null), [src.invoices, today]);
  const people = useMemo(
    () => (src.units && src.residents ? summarizePeople(src.units, src.residents) : null),
    [src.units, src.residents],
  );

  const emergenciesActive = (src.emergencies ?? []).filter((e) => e.status === 'ACTIVE').length;
  const newComplaints = src.complaints ? src.complaints.filter((c) => c.status === 'NEW').length : null;
  const allFailed = loaded && Object.values(src).every((v) => v === null);

  const health: HealthNumbers | null = loaded
    ? {
        rateToday: pace && pace.current.billedCents > 0 ? pace.rateToday : null,
        rateMonth: pace?.current.month ?? `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
        rateTrend: billing
          ? billing.trend.filter((m) => m.collectedCents + m.outstandingCents > 0).map((m) => ({ month: m.month, rate: m.rate }))
          : [],
        arrearsCents: billing ? billing.arrears.reduce((s, a) => s + a.outstandingCents, 0) : null,
        arrearsHouses: billing?.arrears.length ?? 0,
        registeredUnits: people?.unitsWithResidents ?? null,
        livedInUnits: people?.occupied ?? 0,
        pendingBookings: src.bookings?.length ?? null,
        newComplaints,
      }
    : null;

  const groups = useMemo(
    () => groupActions({
      emergencies: src.emergencies ?? [],
      bookings: src.bookings ?? [],
      complaints: src.complaints ?? [],
      arrears: billing?.arrears ?? [],
      today,
    }),
    [src.emergencies, src.bookings, src.complaints, billing, today],
  );
  const feed = useMemo(
    () => activityFeed({
      invoices: src.invoices ?? [],
      units: src.units ?? [],
      residents: src.residents ?? [],
      complaints: src.complaints ?? [],
      bookings: src.bookings ?? [],
      today,
    }),
    [src, today],
  );
  const complaints = src.complaints ? complaintsThisMonth(src.complaints, today) : null;
  const avgResolveHours = src.complaints ? averageResolveHours(src.complaints, today) : null;

  return (
    <Stack spacing={3}>
      <Typography variant="h4">
        {t(greetingKey())}{identity?.name ? `, ${identity.name}.` : '.'}
      </Typography>

      {emergenciesActive > 0 ? (
        <Alert
          severity="error"
          variant="filled"
          action={<Button color="inherit" size="small" component={RouterLink} to="/operations">{t('dash.emergencyOpen')}</Button>}
        >
          {t('dash.emergencyBanner', { count: emergenciesActive })}
        </Alert>
      ) : null}

      {allFailed ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={retry}>{t('dash.retry')}</Button>}>
          {t('dash.statsFailed')}
        </Alert>
      ) : null}

      <HealthTiles n={health} />

      {/* What to do now, beside how this month's collection is going. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
        {loaded ? (
          <GroupedActions
            groups={groups}
            onBookingApproved={(id) => setSrc((s) => ({ ...s, bookings: s.bookings?.filter((b) => b.id !== id) ?? null }))}
          />
        ) : null}
        {pace ? <CollectionPaceCard pace={pace} /> : null}
      </Box>

      {/* What just happened, beside how complaints are handled this month. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
        {loaded ? <ActivityFeed events={feed} now={today} /> : null}
        {complaints ? (
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardActionArea component={RouterLink} to="/complaints" sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  {t('dash.complaintsThisMonth')}
                </Typography>
                {complaints.created === 0 ? (
                  <Typography variant="body2" color="text.secondary">{t('dash.noComplaintsThisMonth')}</Typography>
                ) : (
                  <>
                    <Stack direction="row" spacing={4} sx={{ mb: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('dash.complaintsDone')}</Typography>
                        <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.3 }}>
                          {complaints.done}
                          <Typography component="span" variant="body2" color="text.secondary">
                            {' / '}{complaints.created}
                          </Typography>
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{t('dash.complaintsOpen')}</Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.3, color: complaints.waiting > 0 ? 'warning.main' : 'text.primary' }}
                        >
                          {complaints.waiting + complaints.inProgress}
                        </Typography>
                      </Box>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.round(complaints.doneRate * 100)}
                      sx={{ height: 9, borderRadius: 5, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: 'secondary.main' } }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', fontVariantNumeric: 'tabular-nums' }}>
                      {t('dash.complaintsBreakdown', {
                        pct: Math.round(complaints.doneRate * 100),
                        waiting: complaints.waiting,
                        inProgress: complaints.inProgress,
                      })}
                    </Typography>
                  </>
                )}
                {avgResolveHours !== null ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {t('dash.avgResolve', { time: formatHours(avgResolveHours, t) })}
                  </Typography>
                ) : null}
              </CardContent>
            </CardActionArea>
          </Card>
        ) : null}
      </Box>
    </Stack>
  );
}

/** "5 jam" under a day, otherwise days with one decimal: "2,5 hari". */
function formatHours(hours: number, t: ReturnType<typeof useT>): string {
  if (hours < 24) return t('dash.hours', { count: hours });
  const days = Math.round((hours / 24) * 10) / 10;
  return t('dash.days', { count: days.toLocaleString('id-ID') });
}
